import { resetState, setBookingType } from '@src/components/OMS/ManualDealTradeView/ManualDealSlice';
import { openView } from '@src/components/OMS/OMSSlice';
import { OMSView, type OMSViewLaunchDataMap } from '@src/components/OMS/OMSView';
import { resolveUsersSentinel } from '@src/hooks/filters/useUsersFilter';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useUser } from '@src/hooks/useUser';
import { useAppStateDispatch } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import type { OTCDealData } from '@src/types/OTC';
import { getOtcDealDisplayUser } from '@src/utils/OTC/otcDealDisplayUtils';
import type { MinimalSubscriptionResponse } from '@talos/kyoko';
import {
  ACTION,
  BlotterDensity,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEAL,
  DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
  ExportMenuItems,
  filterByColumnMainMenuItems,
  FormControlSizes,
  IconName,
  isGridApiReady,
  MixpanelEvent,
  removeEmptyFilters,
  SubProductTypeEnum,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useConstant,
  useDateRangeFilter,
  useMixpanel,
  usePersistedBlotterTable,
  useSecuritiesContext,
  useWsBlotterTable,
  type BlotterTableFilter,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams, SelectionChangedEvent } from 'ag-grid-enterprise';
import { useCallback, useMemo } from 'react';
import { pipe } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { LOAN_INSPECTOR_DRAWER_TITLE, useInstrumentInspectorDrawer } from '../InstrumentInspectorDrawer';
import type { DealBlotterRequest } from '../otcBlotterTypes';
import { useLoanColumns } from './useLoanColumns';
import { colIDToFilterBuilderKey, onlyServerFilterKeys, useLoanFilter } from './useLoanFilter';
import { useLoanMenu } from './useLoanMenu';

interface FilteredLoansParams {
  /** ID for common blotter type persistence */
  blotterPersistID: string;
  tabLabel?: string;
  defaultFilter: BlotterTableFilter;
  onCloneTab?: () => void;
  initialIsOpen?: boolean;
  /** Whether to persist the filter, defaults to true */
  persistFilter?: boolean;
}

export const FilteredLoans = function FilteredLoans({
  blotterPersistID,
  tabLabel,
  defaultFilter,
  onCloneTab,
  initialIsOpen,
  persistFilter,
}: FilteredLoansParams) {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const { isAuthorized } = useRoleAuth();
  const canSubmitOrder = isAuthorized(ACTION.SUBMIT_ORDER);
  const { Name: currentUserName } = useUser();
  const blotterTag = useConstant(`FILTERED_OTCLoan_${uuid()}`);
  const { securitiesBySymbol } = useSecuritiesContext();
  const { enableLoanOpenTermSupport } = useFeatureFlag();

  const columns = useLoanColumns();

  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    { persistFilter }
  );

  const filteredLoans = useLoanFilter({
    persistedBlotterTable,
    securitiesBySymbol,
    enableLoanOpenTermSupport,
  });

  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filteredLoans;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const { openClause } = filterBuilderAccordion;

  const getExtraMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams) => {
      return filterByColumnMainMenuItems({
        params,
        colIDToFilterBuilderKey,
        openClause,
        mixpanel,
      });
    },
    [mixpanel, openClause]
  );

  const handleBookLoan = useCallback(() => {
    mixpanel.track(MixpanelEvent.BookManualTrade);
    dispatch(resetState());
    dispatch(setBookingType('fixedRateLoan'));
    const launchData: OMSViewLaunchDataMap[OMSView.ManualDealTradeForm] = { bookingType: 'fixedRateLoan' };
    dispatch(
      openView({
        view: OMSView.ManualDealTradeForm,
        launchData: launchData,
      })
    );
  }, [dispatch, mixpanel]);

  const { openInstrumentInspectorDrawer } = useInstrumentInspectorDrawer();

  const openLoanInstrumentInspectorDrawer = useCallback(
    (deal: OTCDealData, onlyIfOpen?: boolean) =>
      openInstrumentInspectorDrawer(deal, LOAN_INSPECTOR_DRAWER_TITLE, onlyIfOpen),
    [openInstrumentInspectorDrawer]
  );

  const loanMenu = useLoanMenu({
    openClause,
    filterableProperties: filterBuilderProps.properties,
    openLoanInstrumentInspectorDrawer,
  });

  const onSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent<OTCDealData>) => {
      if (!isGridApiReady(api)) {
        return;
      }
      const rows = api.getSelectedRows();
      if (rows.length === 1) {
        openLoanInstrumentInspectorDrawer(rows[0], true);
      }
    },
    [openLoanInstrumentInspectorDrawer]
  );

  const columnsWithMenu = useMemo(() => [...columns, ...loanMenu.columns], [columns, loanMenu.columns]);

  const request = useMemo(
    () =>
      ({
        name: DEAL,
        tag: blotterTag,
        sort_by: '-TradeTime',
        SubProductTypes: [SubProductTypeEnum.OTCFixedRateLoan, SubProductTypeEnum.OTCFloatingRateLoan],
        ...removeEmptyFilters(
          onlyServerFilterKeys({
            ...filteredLoans.filter,
            Users: resolveUsersSentinel(filteredLoans.filter.Users, currentUserName),
          })
        ),
      }) satisfies DealBlotterRequest,
    [blotterTag, filteredLoans.filter, currentUserName]
  );

  const normalizeLoanDealPipe = useMemo(
    () =>
      pipe(
        map(
          (response: MinimalSubscriptionResponse<OTCDealData>): MinimalSubscriptionResponse<OTCDealData> => ({
            ...response,
            data: response.data.map(deal => ({
              ...deal,
              displayUser: getOtcDealDisplayUser(deal),
            })),
          })
        )
      ),
    []
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: 'ID',
    columns: columnsWithMenu,
    clientLocalFilter,
    getExtraMainMenuItems,
    persistence: persistedBlotterTable,
    pipe: normalizeLoanDealPipe,
    density: BlotterDensity.Compact,
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
      onSelectionChanged,
      onRowDoubleClicked: ({ data }) => {
        data && openLoanInstrumentInspectorDrawer(data);
      },
      getContextMenuItems: loanMenu.getContextMenuItems,
    },
    exportOptions: {
      name: 'Loans',
      tabLabel,
    },
  });

  const dateRangeFilter = useDateRangeFilter(filteredLoans.filter, filteredLoans.changeFilter);

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        {...dateRangeFilter}
        quickFilterPrefix={
          canSubmitOrder && (
            <Button
              startIcon={IconName.DocumentAdd}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={handleBookLoan}
            >
              Book Loan
            </Button>
          )
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {loanMenu.dialogs}
    </>
  );
};
